import GameModel from "../../GameModel";
import { Settings } from "../../Settings";
import NearbyTileUtil from "../../util/NearbyTileUtil";
import Character from "../character/Character";
import CharacterClass from "../character/CharacterClass";
import PartyCharacter from "../character/PartyCharacter";
import Thruster from "../character/Thruster";
import Grid from "../grid/Grid";
import GridTile from "../grid/GridTile";
import WorldGrid from "../grid/WorldGrid";
import Item from "../item/Item";
import { ItemSpriteIds } from "../item/ItemSpriteIds";
import { ItemTypes } from "../item/ItemTypes";
import Vector from "../math/Vector";
import CharacterSpritesheet from "../sprite/CharacterSpritesheet";
import ItemOverlaySpritesheet from "../sprite/ItemOverlaySpritesheet";
import GameTime from "../time/GameTime";
import Values from "../value/Values";

export default class Crew extends GameModel {
    /**
     * @param {WorldGrid} worldGrid
     * @param {GameTime} gameTime
     * @param {CharacterSpritesheet} characterSpritesheet
     * @param {ItemOverlaySpritesheet} itemOverlaySpritesheet
     * @param {Values} values
     */
    constructor(worldGrid, gameTime, characterSpritesheet, itemOverlaySpritesheet, values) {
        super();

        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;
        /**
         * @constant
         * @type {GameTime}
         */
        this.gameTime = gameTime;
        /**
         * @constant
         * @type {CharacterSpritesheet}
         */
        this.characterSpritesheet = characterSpritesheet;
        /**
         * @constant
         * @type {Values}
         */
        this.values = values;
        /**
         * @constant
         * @type {ItemOverlaySpritesheet}
         */
        this.itemOverlaySpritesheet = itemOverlaySpritesheet;

        /**
         * @type {PartyCharacter}
         */
        this.main = null;

        /**
         * @constant
         * @type {PartyCharacter[]}
         */
        this.miners = [];

        /**
         * @type {GridTile}
         */
        this.centerTile = null;
        /**
         * @constant
         * @private
         * @type {Vector}
         */
        this.workingCenter = new Vector(0, 0);
    }

    resetTargets() {
        for (let i = 0; i < this.miners.length; i++) {
            this.miners[i].target.resetTarget();
        }
    }

    // resetTurnStates() {
    //     for (let i = 0; i < this.miners.length; i++) {
    //         this.miners[i].resetTurnState();
    //     }
    // }

    /**
     * 
     * @param {Character} character 
     * @returns {number}
     */
    findMinerIndex(character) {
        return this.miners.indexOf(character);
    }

    /**
     * @param {Vector} outputVector 
     */
    calculateCrewCenterCoordinate(outputVector) {
        if (this.miners.length === 1) {
            outputVector.copy(this.miners[0].position.worldCoordinateOrigin);
            return;
        }

        let x = 0;
        let y = 0;

        for (let i = 0; i < this.miners.length; i++) {
            const pos = this.miners[i].position.worldCoordinateOrigin;
            x += pos.x;
            y += pos.y;
        }

        // Get the average coordinate
        x = x / this.miners.length;
        y = y / this.miners.length;


        outputVector.x = x;
        outputVector.y = y;
    }

    /**
     * @returns {GridTile}
     */
    calculateCrewCenterTile() {

        if (this.miners.length === 1) {
            const mainTile = this.miners[0].position.tile;
            if (mainTile) {
                this.centerTile = mainTile;
                return mainTile;
            }

            // The tile of the main miner is sometimes null.
            this.centerTile = this.worldGrid.findGridTile(this.miners[0].position.worldCoordinateOrigin);
            return this.centerTile;
        }
        else {
            this.calculateCrewCenterCoordinate(this.workingCenter);

            const tile = this.worldGrid.findGridTile(this.workingCenter);
            if (tile) {
                this.centerTile = tile;
                return tile;
            }
            else {
                console.log('Crew.getCrewCenterTile() failed to find tile: ' + this.workingCenter.toString());
            }

            // If we failed to find the actual center, let's just use the tile of one of the miners.
            for (let i = 0; i < this.miners.length; i++) {
                const currentTile = this.miners[i].position.tile;
                if (currentTile) {
                    this.centerTile = currentTile;
                    return this.centerTile;                    
                }
            }

            return null;
        }

    }

    /**
     * @returns {PartyCharacter}
     */
    createMainMiner() {
        this.main = new PartyCharacter(new CharacterClass(this.characterSpritesheet), this.values, this.miners.length);
        this.initializeCharacter(this.main);
        this.miners.push(this.main);        

        return this.main;
    }

    /**
     * @param {string} skillId
     */
    addNewMinerNearMain(skillId) {
        if (!this.main.position.tile) {
            console.log('Crew.addNewMinerNearMain() ERROR main miner does not have a tile.');
            return;
        }

        // Pick a tile near the main miner
        // Do this before we add the miner to the array!
        const nearbyTile = NearbyTileUtil.findAvailableTileNearMainMiner(null, this); // <-- null because our new character is not in the crew yet.

        const character = new PartyCharacter(new CharacterClass(this.characterSpritesheet), this.values, this.miners.length);

        character.skillId = skillId;

        // Setup the character with stuff they need (sprites and things).
        this.initializeCharacter(character);

        this.miners.push(character);

        character.position.setTileAndOriginPosition(nearbyTile, nearbyTile.origin.x, nearbyTile.origin.y);
    }

    /**
     * @returns {PartyCharacter}
     */
    addNewMinerForSaveLoad() {
        const character = new PartyCharacter(new CharacterClass(this.characterSpritesheet), this.values, this.miners.length);

        // Setup the character with stuff they need (sprites and things).
        this.initializeCharacter(character);

        this.miners.push(character);

        return character;
    }

    /**
     * @param {PartyCharacter} character 
     */
    initializeCharacter(character) {

        if (!this.itemOverlaySpritesheet.loaded) {
            console.log('Crew.initializeCharacter() itemOverlaySpritesheet not loaded yet');
        }

        const pickStaticAnimation = this.itemOverlaySpritesheet.getSpriteAnimation(ItemSpriteIds.pick.staticOverlay);
        const pickActiveAnimation = this.itemOverlaySpritesheet.getSpriteAnimation(ItemSpriteIds.pick.activeOverlay);
        const thrusterAnimation = this.itemOverlaySpritesheet.getSpriteAnimation(ItemSpriteIds.thruster.activeOverlay);

        character.pick = new Item(ItemTypes.pick, null, pickStaticAnimation, pickActiveAnimation);
        character.thruster = new Thruster(thrusterAnimation, this.gameTime);
    }

    /**
     * @param {Grid} grid 
     */
    onGridUnload(grid) {
        for (let i = 0; i < this.miners.length; i++) {
            const miner = this.miners[i];
            miner.target.onGridUnload(grid);
        }
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        this.miners.length = 0;
        this.main = null;
        this.centerTile = null;
    }

    /**
     * @override
     */
    resetForPrestige() {
        for (let i = 0; i < this.miners.length; i++) {
            const miner = this.miners[i];
            miner.resetGameModel();

            // Put the miner at the start position.
            ////miner.position.setWorldCoordinateOriginXY(Settings.start.startX, Settings.start.startY);
            this.setInitialMinerPosition(miner, i);
        }

        this.centerTile = null;
    }

    /**
     * @private
     * @param {PartyCharacter} miner
     * @param {number} minerIndex 
     */
    setInitialMinerPosition(miner, minerIndex) {
        
        switch (minerIndex) {
            case 0:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX, Settings.start.startY);
                break;
            case 1:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX - (Settings.tile.size * 2), Settings.start.startY);
                break;
            case 2:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX + (Settings.tile.size * 2), Settings.start.startY);
                break;
            case 3:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX - (Settings.tile.size * 4), Settings.start.startY);
                break;
            case 4:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX + (Settings.tile.size * 4), Settings.start.startY);
                break;
            case 5:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX - (Settings.tile.size * 6), Settings.start.startY);
                break;
            case 6:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX + (Settings.tile.size * 6), Settings.start.startY);
                break;
            case 7:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX - (Settings.tile.size * 8), Settings.start.startY);
                break;
            case 8:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX + (Settings.tile.size * 8), Settings.start.startY);
                break;
            case 9:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX - (Settings.tile.size * 10), Settings.start.startY);
                break;
            case 10:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX + (Settings.tile.size * 10), Settings.start.startY);
                break;

                    
            case 11:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX, Settings.start.startY + (Settings.tile.size * 2));
                break;
            case 12:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX - (Settings.tile.size * 2), Settings.start.startY + (Settings.tile.size * 2));
                break;
            case 13:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX + (Settings.tile.size * 2), Settings.start.startY + (Settings.tile.size * 2));
                break;
            case 14:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX - (Settings.tile.size * 4), Settings.start.startY + (Settings.tile.size * 2));
                break;
            case 15:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX + (Settings.tile.size * 4), Settings.start.startY + (Settings.tile.size * 2));
                break;
            case 16:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX - (Settings.tile.size * 6), Settings.start.startY + (Settings.tile.size * 2));
                break;
            case 17:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX + (Settings.tile.size * 6), Settings.start.startY + (Settings.tile.size * 2));
                break;
            case 18:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX - (Settings.tile.size * 8), Settings.start.startY + (Settings.tile.size * 2));
                break;
            case 19:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX + (Settings.tile.size * 8), Settings.start.startY + (Settings.tile.size * 2));
                break;
            case 20:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX - (Settings.tile.size * 10), Settings.start.startY + (Settings.tile.size * 2));
                break;
            case 21:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX + (Settings.tile.size * 10), Settings.start.startY + (Settings.tile.size * 2));
                break;

            default:
                miner.position.setWorldCoordinateOriginXY(Settings.start.startX, Settings.start.startY);
                break;
        }
    }

}